import type { Pagination } from 'src/types/common';
import type { ContentItem, ContentView } from 'src/types/content-view';

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

export interface ContentState {
  // Content Blocks
  contentViews: ContentView[] | null;
  currentContentView: ContentView | null;
  contentViewsPagination: Pagination<ContentView>['pagination'] | null;

  // Content Items (Flat Structure)
  contentItems: Record<string, ContentItem>; // Map of id -> ContentItem
  selectedContentItem: ContentItem | null;

  // UI State
  loading: Record<string, boolean>;
  error: string | null;

  // Actions
  setContentViews: (
    contentViews: ContentView[],
    pagination?: Pagination<ContentView>['pagination']
  ) => void;
  setCurrentContentView: (contentView: ContentView | null) => void;
  setContentItems: (contentItems: ContentItem[]) => void;
  addContentItem: (contentItem: ContentItem) => void;
  addContentItems: (contentItems: ContentItem[]) => void;
  updateContentItem: (id: string, updates: Partial<ContentItem>) => void;
  removeContentItem: (id: string) => void;
  setSelectedContentItem: (contentItem: ContentItem | null) => void;
  setLoading: (key: string, loading: boolean) => void;
  setError: (error: string | null) => void;
  getChildren: (parentId: string | null) => ContentItem[];
  getRootItems: () => ContentItem[];
  getAncestors: (itemId: string) => ContentItem[];
  getDescendants: (itemId: string) => ContentItem[];
  reorderItems: (parentId: string | null, newOrder: string[]) => void;
  clearContentItems: () => void;
  clearAll: () => void;
}

const initialContentState = {
  contentViews: null,
  currentContentView: null,
  contentViewsPagination: null,
  contentItems: {},
  selectedContentItem: null,
  loading: {},
  error: null,
};

export const contentModel = create(
  subscribeWithSelector<ContentState>((set, get) => ({
    ...initialContentState,

    // Actions
    setContentViews: (
      contentViews: ContentView[],
      pagination?: Pagination<ContentView>['pagination']
    ) => {
      set({
        contentViews,
        contentViewsPagination: pagination || null,
      });
    },

    setCurrentContentView: (contentView: ContentView | null) => {
      set({ currentContentView: contentView });
    },

    setContentItems: (contentItems: ContentItem[]) => {
      const itemsMap: Record<string, ContentItem> = {};
      contentItems.forEach((item) => {
        itemsMap[item.itemId] = item;
      });
      set({ contentItems: itemsMap });
    },

    addContentItem: (contentItem: ContentItem) => {
      const { contentItems } = get();
      set({
        contentItems: {
          ...contentItems,
          [contentItem.itemId]: contentItem,
        },
      });
    },

    addContentItems: (newContentItems: ContentItem[]) => {
      const { contentItems } = get();

      const newItemsMap = { ...contentItems };

      // Add all items to the map at once
      newContentItems.forEach((item) => {
        newItemsMap[item.itemId] = item;
      });

      set({ contentItems: newItemsMap });
    },

    updateContentItem: (id: string, updates: Partial<ContentItem>) => {
      const { contentItems } = get();
      const existingItem = contentItems[id];
      if (existingItem) {
        set({
          contentItems: {
            ...contentItems,
            [id]: { ...existingItem, ...updates },
          },
        });
      }
    },

    removeContentItem: (id: string) => {
      const { contentItems } = get();
      const newItems = { ...contentItems };
      delete newItems[id];
      set({ contentItems: newItems });
    },

    setSelectedContentItem: (contentItem: ContentItem | null) => {
      set({ selectedContentItem: contentItem });
    },

    setLoading: (key: string, loading: boolean) => {
      const { loading: currentLoading } = get();
      set({
        loading: {
          ...currentLoading,
          [key]: loading,
        },
      });
    },

    setError: (error: string | null) => {
      set({ error });
    },

    // Helper methods for flat structure
    getChildren: (parentId: string | null): ContentItem[] => {
      const { contentItems } = get();
      return Object.values(contentItems).filter((item) => item.parentItemId === parentId);
    },

    getRootItems: (): ContentItem[] => {
      const state = get();
      return state.getChildren(null);
    },

    getAncestors: (itemId: string): ContentItem[] => {
      const { contentItems } = get();
      const ancestors: ContentItem[] = [];
      let current = contentItems[itemId];

      while (current && current.parentItemId) {
        const parent = contentItems[current.parentItemId];
        if (parent) {
          ancestors.unshift(parent);
          current = parent;
        } else {
          break;
        }
      }

      return ancestors;
    },

    getDescendants: (itemId: string): ContentItem[] => {
      const state = get();
      const descendants: ContentItem[] = [];
      const queue = [itemId];

      while (queue.length > 0) {
        const currentId = queue.shift()!;
        const children = state.getChildren(currentId);

        children.forEach((child: ContentItem) => {
          descendants.push(child);
          queue.push(child.itemId);
        });
      }

      return descendants;
    },

    reorderItems: (parentId: string | null, newOrder: string[]) => {
      const { contentItems } = get();
      const updatedItems = { ...contentItems };

      newOrder.forEach((itemId, index) => {
        if (updatedItems[itemId]) {
          updatedItems[itemId] = {
            ...updatedItems[itemId],
            order: index,
            parentItemId: parentId,
          };
        }
      });

      set({ contentItems: updatedItems });
    },

    clearContentItems: () => {
      set({ contentItems: {} });
    },

    clearAll: () => {
      set(initialContentState);
    },
  }))
);
